export interface ScoreboardEventPlayer {
  id: string
  name_first: string
  name_last: string
  full_name: string
  player_number: number
  image: { full_path: string } | null
}

export interface ScoreboardEventPeriod {
  id?: string
  duration?: number
  type?: {
    id?: string
    name?: string
    name_full?: string
    is_overtime?: boolean
    sort_order?: number
  }
}

export interface ScoreboardEvent {
  event_type?: string
  occurred_at: string
  period_clock_time?: string
  period?: ScoreboardEventPeriod
  team_id?: string
  player_id?: string
  player?: ScoreboardEventPlayer
  value?: number
  goal_type?: string | null
  assists?: Array<{
    id?: string
    player?: ScoreboardEventPlayer
    type?: string
  }>
  goalie_id?: string | null
  goalie?: ScoreboardEventPlayer | null
  previous_goalie_id?: string | null
  previous_goalie?: ScoreboardEventPlayer | null
  offense_type?: { id?: number; name?: string } | null
  offense_severity?: {
    id?: number
    name?: string
    duration?: number | null
  } | null
  home_team_score?: number
  visiting_team_score?: number
  home_team_shots?: number
  visiting_team_shots?: number
}

export interface ScoreboardSnapshot {
  homeScore: number
  visitorScore: number
  homeShots: number
  visitorShots: number
  displayTimer: string
  displayPeriod: string
}

export interface ScoreboardSyncResult extends ScoreboardSnapshot {
  isPastLastEvent: boolean
}

export interface ComputeScoreboardAtTimeParams {
  currentTimeSec: number
  streamStartedAt: string | null | undefined
  isLive: boolean
  scoreboardEvents: ScoreboardEvent[] | null | undefined
}

/**
 * Pure function that maps a video playback position to a scoreboard snapshot.
 * Walks the pre-sorted events array to find the latest event that occurred
 * before the given video time, then returns cumulative scores/shots/period.
 *
 * Returns null when required timestamps are missing (caller should skip update).
 */
export const computeScoreboardAtTime = ({
  currentTimeSec,
  streamStartedAt,
  isLive,
  scoreboardEvents,
}: ComputeScoreboardAtTimeParams): ScoreboardSyncResult | null => {
  if (!isLive && !streamStartedAt) {
    return null
  }

  // For live streams use real time; for replay map video currentTime to stream start.
  const currentEventTimeMs = isLive
    ? Date.now()
    : Math.round(new Date(streamStartedAt!).getTime() + currentTimeSec * 1000)

  // Find the latest event that occurred before or at the current video time.
  let latestEvent: ScoreboardEvent | null = null

  if (scoreboardEvents) {
    for (let i = 0; i < scoreboardEvents.length; i++) {
      const e = scoreboardEvents[i]
      if (!e.occurred_at) {
        continue
      }
      if (new Date(e.occurred_at).getTime() > currentEventTimeMs) {
        break
      }
      latestEvent = e
    }
  }

  const isGameEnd = latestEvent?.event_type === 'game_end'

  let displayTimer = '00:00'
  if (!isGameEnd && latestEvent?.period_clock_time) {
    displayTimer = latestEvent.period_clock_time
    if (displayTimer.startsWith('00:')) {
      displayTimer = displayTimer.slice(3)
    }
  }

  const isPastLastEvent =
    !isLive &&
    (scoreboardEvents?.length ?? 0) > 0 &&
    currentEventTimeMs >
      new Date(
        scoreboardEvents![scoreboardEvents!.length - 1].occurred_at
      ).getTime()

  return {
    homeScore: latestEvent?.home_team_score ?? 0,
    visitorScore: latestEvent?.visiting_team_score ?? 0,
    homeShots: latestEvent?.home_team_shots ?? 0,
    visitorShots: latestEvent?.visiting_team_shots ?? 0,
    displayTimer: isGameEnd ? 'Final' : displayTimer,
    displayPeriod: isGameEnd ? '' : latestEvent?.period?.type?.name || '',
    isPastLastEvent,
  }
}

/**
 * Returns the scoreboard state from the last event in a pre-sorted events array.
 * Useful for getting the "current" scoreboard state without video time sync (e.g. live games).
 */
export const getLatestScoreboardState = (
  events: ScoreboardEvent[] | null | undefined
): ScoreboardSnapshot | null => {
  if (!events || events.length === 0) {
    return null
  }
  const lastEvent = events[events.length - 1]
  const isGameEnd = lastEvent.event_type === 'game_end'

  let displayTimer = '00:00'
  if (!isGameEnd && lastEvent.period_clock_time) {
    displayTimer = lastEvent.period_clock_time
    if (displayTimer.startsWith('00:')) {
      displayTimer = displayTimer.slice(3)
    }
  }

  return {
    homeScore: lastEvent.home_team_score ?? 0,
    visitorScore: lastEvent.visiting_team_score ?? 0,
    homeShots: lastEvent.home_team_shots ?? 0,
    visitorShots: lastEvent.visiting_team_shots ?? 0,
    displayTimer: isGameEnd ? 'Final' : displayTimer,
    displayPeriod: isGameEnd ? '' : lastEvent.period?.type?.name || '',
  }
}
